import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChartLine, Check } from "@phosphor-icons/react";

/**
 * iter v28 r402 — User M1311: «stima canone affitto e anche prezzo vendita
 * in qualche modo carino durante creazione annuncio. È una stima
 * approssimativa».
 *
 * Mostra una stima di mercato compatta quando l'agente sta creando o
 * modificando un annuncio. Chiama POST /api/estimate/value con i campi
 * minimi compilati e ritorna range Min-Max + bottone "Usa questa stima".
 *
 * Props minime: listingType, city, surface, propertyType.
 * Optional: state, floor, hasElevator, energyClass.
 *
 * UX: niente AVM completo, solo una nota onesta sotto al campo prezzo.
 * Si nasconde se mancano i campi minimi necessari.
 */
export default function PriceHintBox({
  listingType,     // "vendita" | "affitto"
  city,
  surface,
  propertyType,
  state,
  floor,
  hasElevator,
  energyClass,
  currentPrice,
  onUsePrice,
  className = "",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAffitto = (listingType || "").toLowerCase() === "affitto";

  // Debounced fetch quando i campi cambiano
  useEffect(() => {
    const surfaceNum = Number(surface);
    if (!city || !surfaceNum || surfaceNum < 10 || !propertyType) {
      setData(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          city: String(city).trim(),
          surface_sqm: surfaceNum,
          property_type: propertyType,
        };
        if (state) payload.state = state;
        if (floor !== "" && floor != null) payload.floor = Number(floor);
        if (hasElevator != null) payload.has_elevator = !!hasElevator;
        if (energyClass) payload.energy_class = energyClass;
        const { data: r } = await api.post("/estimate/value", payload);
        setData(r);
      } catch (e) {
        setError(e?.response?.data?.detail || "Impossibile calcolare la stima");
        setData(null);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [city, surface, propertyType, state, floor, hasElevator, energyClass]);

  if (!city || !surface || !propertyType) return null;
  if (loading) {
    return (
      <div className={`text-[11.5px] text-[#9A968B] italic ${className}`}>
        Sto calcolando la stima di mercato…
      </div>
    );
  }
  if (error || !data) return null;

  const suggestedPrice = isAffitto ? data.rent_monthly : data.estimated;
  const minPrice = isAffitto ? data.rent_monthly_min : data.range_min;
  const maxPrice = isAffitto ? data.rent_monthly_max : data.range_max;
  if (!suggestedPrice) return null;

  const fmt = (n) => {
    const num = Number(n);
    if (isAffitto) {
      return `€ ${num.toLocaleString("it-IT")}/mese`;
    }
    if (num >= 1000000) return `€ ${(num / 1000000).toFixed(2).replace(".", ",")}M`;
    if (num >= 10000) return `€ ${Math.round(num / 1000)}k`;
    return `€ ${num.toLocaleString("it-IT")}`;
  };

  // L'agente ha già scritto un prezzo che è MOLTO diverso dalla stima?
  // Mostra un piccolo avviso onesto.
  const currentNum = Number(currentPrice || 0);
  const isOff = currentNum > 0 && (currentNum < minPrice * 0.85 || currentNum > maxPrice * 1.15);

  return (
    <div
      className={`bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-lg px-3 py-2.5 ${className}`}
      data-testid={`price-hint-${isAffitto ? "rent" : "sale"}`}
    >
      <div className="flex items-start gap-2.5">
        <ChartLine size={14} weight="bold" className="text-[#5C6B4E] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-[#5C6B4E]">
            {isAffitto ? "Canone di mercato stimato" : "Prezzo di mercato stimato"}
          </div>
          <div className="text-[13.5px] font-semibold text-[#1C1C1A] mt-0.5 leading-tight">
            {fmt(minPrice)} – {fmt(maxPrice)}
          </div>
          <div className="text-[11px] text-[#6B6A65] mt-0.5">
            media {fmt(suggestedPrice)}
            <span className="text-[#9A968B] ml-1.5">· confidenza {data.confidence || "media"}</span>
          </div>
          {isOff && (
            <div className="text-[10.5px] text-[#B45309] mt-1 leading-snug">
              Il prezzo che hai scritto è fuori dal range di mercato. È volontario? OK, è solo un'indicazione.
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            {onUsePrice && (
              <button
                type="button"
                onClick={() => onUsePrice(Math.round(suggestedPrice))}
                className="text-[11px] font-semibold text-[#5C6B4E] hover:text-[#1C1C1A] inline-flex items-center gap-1"
                data-testid="use-estimate-btn"
              >
                <Check size={11} weight="bold" /> Usa {fmt(suggestedPrice)}
              </button>
            )}
            <span className="text-[10px] text-[#9A968B] italic">Stima approssimativa OMI 2° semestre 2025</span>
          </div>
        </div>
      </div>
    </div>
  );
}
